import { ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import { wasAborted } from "../utilities/abortedRequests";
import { useSearchParams } from "react-router-dom";
import { useModalContext } from "../components/ModalProvider";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { authenticationAtom } from "../State";
import { userAPI } from "../UserAPI";
import dayjs from "dayjs";
import Section from "../components/Section";
import { useUrlPageState } from "../utilities/dataTablePagination";


import ReactApexChart from "react-apexcharts";
import { useCustomerTheme } from "../Theme";
import Metric from "../components/Metric";
import { useFormats } from "../hooks/formats";
import InfoList from "../object-properties/InfoList";

import type { Column, ColumnDef } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface Properties {}

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({
  column,
  children,
}: {
  column: Column<any, unknown>;
  children: ReactNode;
}) {
  const sorted = column.getIsSorted();

  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {children}
      {sorted === "asc" ? (
        <ArrowUp aria-hidden className="h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown aria-hidden className="h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
      )}
    </Button>
  );
}

/** Leerzustand: Überschrift, Erklärung und — wo sinnvoll — Handlungsaufforderung. */
function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-4 text-center">
      <div className="text-sm font-semibold text-foreground">{title}</div>
      {hint ? (
        <div className="max-w-prose text-sm text-muted-foreground">{hint}</div>
      ) : null}
      {action}
    </div>
  );
}

/** Platzhalter für ein noch ladendes Diagramm. */
function ChartSkeleton({ className }: { className?: string }) {
  return <Skeleton className={className ?? "h-[300px] w-full"} />;
}

/** Platzhalter für eine noch ladende Kennzahlenspalte. */
function MetricsSkeleton({ count }: { count: number }) {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="flex w-[200px] flex-col items-center gap-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-8 w-24" />
        </div>
      ))}
    </div>
  );
}

/**
 * Radix erlaubt keinen leeren `value` in `SelectItem`. Der Bestandszustand
 * kennt „kein Angebot gewählt“ als `null` — der Sentinel transportiert das
 * nur durch die Auswahl.
 */
const NO_OFFER = "__none";

export default function StatisticsPanel(_properties: Properties) {
  const { t } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const { pushError, showMessageDialog: _showMessageDialog } = useModalContext() as any;
  const { currencyFormat, numberFormat1Digit } = useFormats();
  const { theme } = useCustomerTheme();
  const [colors] = useState([
    theme.secondary,
    theme.tertiary,
    theme.primary,
    theme.secondaryLight,
    theme.tertiaryLight,
    theme.primaryLight,
  ]);
  const [meteringStatistics, setMeteringStatistics] = useState() as any;
  const [inboundStatistics, setInboundStatistics] = useState() as any;
  const [publishedMarketplaceOffers, setPublishedMarketplaceOffers] = useState<Array<{ _id: string; name: string }>>([]);
  const [publishedOffersLoading, setPublishedOffersLoading] = useState(true);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const [offerStatistics, setOfferStatistics] = useState<any>(null);
  const [offerStatisticsLoading, setOfferStatisticsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // The active tab is derived directly from the URL — the URL is the single
  // source of truth. A previous version mirrored it into a `tab` useState and
  // kept the two in sync with two effects (URL→state and state→URL). Whenever
  // the two momentarily disagreed (e.g. right after a click) both effects fired
  // on the same render in opposite directions and *swapped* the values instead
  // of converging, live-locking the panel into an infinite re-render that
  // remounted every chart (visible as flickering charts). Deriving the tab and
  // routing every change through the URL removes the second writer entirely.
  const tab = searchParams.get("tab") || "marketplace";
  const setTab = useCallback(
    (nextTab: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", nextTab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const fieldId = useId();
  const marketingPagination = useUrlPageState("marketingPage");
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailDialogTitle, setDetailDialogTitle] = useState("");
  const [detailDialogContent, setDetailDialogContent] = useState<JSX.Element | null>(null);

  const refreshMarketplaceStats = useCallback(async (signal?: AbortSignal, preserveSelection = false) => {
    if (!authentication?.customer) return;
    try {
      setPublishedOffersLoading(true);
      const offers = await userAPI.getMarketplaceOffers(authentication.customer, signal);
      const publishedAndOffered = offers.filter(
        (o: any) => o.published === true && o.offered === true
      );
      const offerList = publishedAndOffered.map((o: any) => ({ _id: o._id, name: o.name || o._id }));
      setPublishedMarketplaceOffers(offerList);
      if (publishedAndOffered.length > 0) {
        setSelectedOfferId((prev) => {
          const stillInList = publishedAndOffered.some((o: any) => o._id === prev);
          return preserveSelection && stillInList ? prev : publishedAndOffered[0]._id;
        });
      } else {
        setSelectedOfferId(null);
      }
    } catch (error: any) {
      if (wasAborted(error)) return;

      console.error("Failed to fetch marketplace offers:", error);
      pushError(error);
      setPublishedMarketplaceOffers([]);
    } finally {
      setPublishedOffersLoading(false);
    }
  }, [authentication?.customer, pushError]);

  const refreshOfferStatistics = useCallback(async () => {
    if (!selectedOfferId || !authentication?.customer) {
      setOfferStatistics(null);
      return;
    }
    try {
      setOfferStatisticsLoading(true);
      const stats = await userAPI.getMarketplaceOfferStatistics(
        authentication.customer,
        selectedOfferId
      );
      setOfferStatistics(stats);
    } catch (error: any) {
      const is404 = error?.message?.includes("Not Found") || error?.response?.status === 404;
      if (!is404) {
        console.error("Failed to fetch offer statistics:", error);
        pushError(error);
      }
      setOfferStatistics(null);
    } finally {
      setOfferStatisticsLoading(false);
    }
  }, [authentication?.customer, selectedOfferId, pushError]);

  const handleRefreshMarketplace = useCallback(async () => {
    await refreshMarketplaceStats(undefined, true);
    await refreshOfferStatistics();
  }, [refreshMarketplaceStats, refreshOfferStatistics]);

  /**
   * Die jeweils neuesten Rückrufe, für Effekte, die nicht an ihren Kennungen hängen dürfen.
   *
   * Ein Ladeeffekt, der einen Rückruf in seiner Abhängigkeitsliste führt, läuft erneut, sobald
   * dessen Kennung wechselt. `pushError` hängt an `t` von i18next, `refreshMarketplaceStats` und
   * `refreshOfferStatistics` hängen an `pushError` — wird eine dieser Kennungen instabil, entsteht
   * eine Schleife aus Laden, Zustand setzen und wieder Laden. Sie verbraucht keine Rechenzeit, sie
   * wartet ja am Netz, und lähmt die Seite trotzdem: genau das Bild, das ein hängender Reiter
   * zeigt, dessen Prozess bei null Prozent steht.
   *
   * Über einen Ref gelesen, hängt der Effekt nur noch an dem, was die Anfrage wirklich bestimmt.
   */
  const latestRef = useRef({ refreshMarketplaceStats, refreshOfferStatistics, handleRefreshMarketplace, pushError });

  latestRef.current = { refreshMarketplaceStats, refreshOfferStatistics, handleRefreshMarketplace, pushError };

  useEffect(() => {
    /*
     * Was dieser Bereich anfragt, endet mit ihm.
     *
     * Ohne das laufen die Anfragen des verlassenen Bereichs weiter und besetzen die wenigen
     * Verbindungen, die der Browser je Host hält — die des neuen stellen sich dahinter an, und der
     * Inhaltswechsel wartet auf Ladevorgänge, die niemanden mehr interessieren.
     */
    const abbruch = new AbortController();

    const fetchData = async () => {
      // Fetch metering statistics for current month
      try {
        const from = dayjs().startOf("M").toDate();
        const to = dayjs().endOf("M").toDate();
        
        // Fetch outbound statistics
        const statistics = await userAPI.getMeteringStatistics(
          authentication.customer,
          from,
          to,
          abbruch.signal
        );
        console.log("Metering statistics >>>", statistics);
        setMeteringStatistics(statistics);
        
        // Fetch inbound statistics
        const inboundStats = await userAPI.getInboundMeteringStatistics(
          authentication.customer,
          from,
          to,
          abbruch.signal
        );
        console.log("Inbound statistics >>>", inboundStats);
        setInboundStatistics(inboundStats);
      } catch (error: any) {
        // Den Bereich verlassen zu haben ist kein Fehler.
        if (wasAborted(error)) return;

        console.error("Failed to fetch metering statistics:", error);
        latestRef.current.pushError(error);
      }
    };

    fetchData();
    void latestRef.current.refreshMarketplaceStats(abbruch.signal);

    return () => abbruch.abort();
    // Geladen wird, wenn der Mandant wechselt — und sonst nicht. Die Rückrufe kommen aus dem Ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer]);

  // Auto-refresh marketplace stats every 15 minutes when on marketplace tab
  useEffect(() => {
    if (tab !== "marketplace") return;
    const interval = setInterval(() => {
      void latestRef.current.handleRefreshMarketplace();
    }, 15 * 60 * 1000);
    return () => clearInterval(interval);
    // Ohne den Rückruf in den Abhängigkeiten: Sonst würde das Intervall bei jeder neuen Kennung
    // verworfen und neu gesetzt — und liefe im ungünstigen Fall nie ab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Clear selected offer if it's no longer in the published list
  useEffect(() => {
    if (
      selectedOfferId &&
      publishedMarketplaceOffers.length > 0 &&
      !publishedMarketplaceOffers.some((o) => o._id === selectedOfferId)
    ) {
      setSelectedOfferId(null);
    }
  }, [selectedOfferId, publishedMarketplaceOffers]);

  // Fetch detailed offer statistics when an offer is selected
  useEffect(() => {
    void latestRef.current.refreshOfferStatistics();
    // An dem, was die Anfrage bestimmt — Mandant und gewähltes Angebot —, nicht an der Kennung des
    // Rückrufs, der sie stellt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer, selectedOfferId]);

  // Prepare data for charts
  const prepareBarChartData = () => {
    if (!meteringStatistics) return { categories: [], series: [], productDataMap: {} };

    // Get all days in current month
    const daysInMonth = dayjs().daysInMonth();
    const monthStart = dayjs().startOf("M");
    const categories: string[] = [];
    const offerDataMap: { [key: string]: { [dayKey: string]: number } } = {};
    const offerLabels: { [key: string]: string } = {};
    const productDataMap: { [key: string]: any } = {};

    // Initialize all days with 0 for categories
    for (let i = 0; i < daysInMonth; i++) {
      const day = monthStart.add(i, "day");
      const _dayKey = day.format("YYYY-MM-DD");
      categories.push(day.format("DD.MM"));
    }

    // Group calls by offer and day
    Object.values(meteringStatistics).forEach((productData: any) => {
      const supplierName = productData.supplier?.name || "Unknown";
      const offerName = productData.offer?.name || "Unknown";
      const label = `${supplierName}: ${offerName}`;
      const key = `${supplierName}|${offerName}`;
      
      if (!offerDataMap[key]) {
        offerDataMap[key] = {};
        offerLabels[key] = label;
        // Store the first productData for this key (they should all have the same offer/plan)
        productDataMap[key] = productData;
      }
      
      if (productData.calls && Array.isArray(productData.calls)) {
        productData.calls.forEach((callEntry: any) => {
          const day = dayjs(callEntry.day).format("YYYY-MM-DD");
          if (!offerDataMap[key][day]) {
            offerDataMap[key][day] = 0;
          }
          offerDataMap[key][day] += callEntry.calls || 0;
        });
      }
    });

    // Create series for each offer
    const series = Object.keys(offerDataMap).map((key) => {
      const data = categories.map((_, index) => {
        const dayKey = monthStart.add(index, "day").format("YYYY-MM-DD");
        return offerDataMap[key][dayKey] || 0;
      });
      return {
        name: offerLabels[key],
        data,
      };
    });

    return {
      categories,
      series,
      productDataMap,
    };
  };

  const preparePieChartData = () => {
    if (!meteringStatistics) return { labels: [], series: [], productDataMap: {} };

    const toolCallMap: { [key: string]: number } = {};
    const productDataMap: { [key: string]: any } = {};

    // Aggregate total calls per tool per marketplace offer
    Object.values(meteringStatistics).forEach((productData: any) => {
      const supplierName = productData.supplier?.name || "Unknown";
      const offerName = productData.offer?.name || "Unknown";
      const label = `${supplierName}: ${offerName}`;
      
      if (productData.calls && Array.isArray(productData.calls)) {
        const totalCalls = productData.calls.reduce(
          (sum: number, entry: any) => sum + (entry.calls || 0),
          0
        );
        if (totalCalls > 0) {
          toolCallMap[label] = (toolCallMap[label] || 0) + totalCalls;
          // Store productData for this label (use the one with the highest calls)
          if (!productDataMap[label] || totalCalls > (productDataMap[label].totalCalls || 0)) {
            productDataMap[label] = { ...productData, totalCalls };
          }
        }
      }
    });

    return {
      labels: Object.keys(toolCallMap),
      series: Object.values(toolCallMap),
      productDataMap,
    };
  };

  const prepareCostPieChartData = () => {
    if (!meteringStatistics) return { labels: [], series: [], productDataMap: {} };

    const costMap: { [key: string]: number } = {};
    const productDataMap: { [key: string]: any } = {};
    const MIN_COST = 0.0001; // Minimum cost to avoid division by zero

    // Aggregate total costs per tool per marketplace offer
    Object.values(meteringStatistics).forEach((productData: any) => {
      const supplierName = productData.supplier?.name || "Unknown";
      const offerName = productData.offer?.name || "Unknown";
      const label = `${supplierName}: ${offerName}`;
      
      // Get cost from productData, default to MIN_COST if 0 or undefined
      const cost = productData.cost !== undefined && productData.cost !== null 
        ? (productData.cost > 0 ? productData.cost : MIN_COST)
        : MIN_COST;
      
      costMap[label] = (costMap[label] || 0) + cost;
      // Store productData for this label
      if (!productDataMap[label]) {
        productDataMap[label] = productData;
      }
    });

    return {
      labels: Object.keys(costMap),
      series: Object.values(costMap),
      productDataMap,
    };
  };

  const barChartData = prepareBarChartData();
  const pieChartData = preparePieChartData();
  const costPieChartData = prepareCostPieChartData();

  // Calculate total calls, total cost, and total offers
  const calculateTotals = () => {
    if (!meteringStatistics) return { totalCalls: 0, totalCost: 0, totalOffers: 0 };

    let totalCalls = 0;
    let totalCost = 0;
    const uniqueOffers = new Set<string>();

    Object.values(meteringStatistics).forEach((productData: any) => {
      // Sum up all calls
      if (productData.calls && Array.isArray(productData.calls)) {
        const calls = productData.calls.reduce(
          (sum: number, entry: any) => sum + (entry.calls || 0),
          0
        );
        totalCalls += calls;
      }

      // Sum up costs (use 0 if cost is 0 or undefined, don't add MIN_COST here)
      const cost = productData.cost !== undefined && productData.cost !== null 
        ? productData.cost 
        : 0;
      totalCost += cost;

      // Count unique offers
      const supplierName = productData.supplier?.name || "Unknown";
      const offerName = productData.offer?.name || "Unknown";
      const offerKey = `${supplierName}|${offerName}`;
      uniqueOffers.add(offerKey);
    });

    return { totalCalls, totalCost, totalOffers: uniqueOffers.size };
  };

  const { totalCalls, totalCost, totalOffers } = calculateTotals();

  // Prepare data for inbound calls charts (using byTool)
  const prepareInboundBarChartData = () => {
    if (!inboundStatistics?.byTool) return { categories: [], series: [], productDataMap: {} };

    const daysInMonth = dayjs().daysInMonth();
    const monthStart = dayjs().startOf("M");
    const categories: string[] = [];
    const toolDataMap: { [key: string]: { [dayKey: string]: number } } = {};
    const toolLabels: { [key: string]: string } = {};
    const productDataMap: { [key: string]: any } = {};

    for (let i = 0; i < daysInMonth; i++) {
      const day = monthStart.add(i, "day");
      const _dayKey = day.format("YYYY-MM-DD");
      categories.push(day.format("DD.MM"));
    }

    Object.values(inboundStatistics.byTool).forEach((toolData: any) => {
      const offerName = toolData.offer?.name || "Unknown";
      const label = offerName;
      const key = offerName;
      
      if (!toolDataMap[key]) {
        toolDataMap[key] = {};
        toolLabels[key] = label;
        productDataMap[key] = toolData;
      }
      
      if (toolData.calls && Array.isArray(toolData.calls)) {
        toolData.calls.forEach((callEntry: any) => {
          const day = dayjs(callEntry.day).format("YYYY-MM-DD");
          if (!toolDataMap[key][day]) {
            toolDataMap[key][day] = 0;
          }
          toolDataMap[key][day] += callEntry.calls || 0;
        });
      }
    });

    const series = Object.keys(toolDataMap).map((key) => {
      const data = categories.map((_, index) => {
        const dayKey = monthStart.add(index, "day").format("YYYY-MM-DD");
        return toolDataMap[key][dayKey] || 0;
      });
      return {
        name: toolLabels[key],
        data,
      };
    });

    return { categories, series, productDataMap };
  };

  const prepareInboundPieChartData = () => {
    if (!inboundStatistics?.byTool) return { labels: [], series: [], productDataMap: {} };

    const toolCallMap: { [key: string]: number } = {};
    const productDataMap: { [key: string]: any } = {};

    Object.values(inboundStatistics.byTool).forEach((toolData: any) => {
      const offerName = toolData.offer?.name || "Unknown";
      const label = offerName;
      
      if (toolData.calls && Array.isArray(toolData.calls)) {
        const totalCalls = toolData.calls.reduce(
          (sum: number, entry: any) => sum + (entry.calls || 0),
          0
        );
        if (totalCalls > 0) {
          toolCallMap[label] = (toolCallMap[label] || 0) + totalCalls;
          if (!productDataMap[label] || totalCalls > (productDataMap[label].totalCalls || 0)) {
            productDataMap[label] = { ...toolData, totalCalls };
          }
        }
      }
    });

    return {
      labels: Object.keys(toolCallMap),
      series: Object.values(toolCallMap),
      productDataMap,
    };
  };

  const prepareInboundCostPieChartData = () => {
    if (!inboundStatistics?.byTool) return { labels: [], series: [], productDataMap: {} };

    const costMap: { [key: string]: number } = {};
    const productDataMap: { [key: string]: any } = {};
    const MIN_COST = 0.0001;

    Object.values(inboundStatistics.byTool).forEach((toolData: any) => {
      const offerName = toolData.offer?.name || "Unknown";
      const label = offerName;
      
      const cost = toolData.cost !== undefined && toolData.cost !== null 
        ? (toolData.cost > 0 ? toolData.cost : MIN_COST)
        : MIN_COST;
      
      costMap[label] = (costMap[label] || 0) + cost;
      if (!productDataMap[label]) {
        productDataMap[label] = toolData;
      }
    });

    return {
      labels: Object.keys(costMap),
      series: Object.values(costMap),
      productDataMap,
    };
  };

  const calculateInboundTotals = () => {
    if (!inboundStatistics?.byTool) return { totalCalls: 0, totalCost: 0, totalTools: 0 };

    let totalCalls = 0;
    let totalCost = 0;
    const uniqueTools = new Set<string>();

    Object.values(inboundStatistics.byTool).forEach((toolData: any) => {
      if (toolData.calls && Array.isArray(toolData.calls)) {
        const calls = toolData.calls.reduce(
          (sum: number, entry: any) => sum + (entry.calls || 0),
          0
        );
        totalCalls += calls;
      }

      const cost = toolData.cost !== undefined && toolData.cost !== null 
        ? toolData.cost 
        : 0;
      totalCost += cost;

      const offerName = toolData.offer?.name || "Unknown";
      uniqueTools.add(offerName);
    });

    return { totalCalls, totalCost, totalTools: uniqueTools.size };
  };

  // Prepare data for customer charts (using byCustomer)
  const prepareCustomerBarChartData = () => {
    if (!inboundStatistics?.byCustomer) return { categories: [], series: [], customerDataMap: {} };

    const daysInMonth = dayjs().daysInMonth();
    const monthStart = dayjs().startOf("M");
    const categories: string[] = [];
    const customerDataMap: { [key: string]: { [dayKey: string]: number } } = {};
    const customerLabels: { [key: string]: string } = {};
    const customerInfoMap: { [key: string]: any } = {};

    for (let i = 0; i < daysInMonth; i++) {
      const day = monthStart.add(i, "day");
      const _dayKey = day.format("YYYY-MM-DD");
      categories.push(day.format("DD.MM"));
    }

    Object.values(inboundStatistics.byCustomer).forEach((customerData: any) => {
      const customerName = customerData.customer?.name || "Unknown";
      const key = customerData.customer?.id || "unknown";
      
      if (!customerDataMap[key]) {
        customerDataMap[key] = {};
        customerLabels[key] = customerName;
        customerInfoMap[key] = customerData.customer;
      }
      
      if (customerData.calls && Array.isArray(customerData.calls)) {
        customerData.calls.forEach((callEntry: any) => {
          const day = dayjs(callEntry.day).format("YYYY-MM-DD");
          if (!customerDataMap[key][day]) {
            customerDataMap[key][day] = 0;
          }
          customerDataMap[key][day] += callEntry.calls || 0;
        });
      }
    });

    const series = Object.keys(customerDataMap).map((key) => {
      const data = categories.map((_, index) => {
        const dayKey = monthStart.add(index, "day").format("YYYY-MM-DD");
        return customerDataMap[key][dayKey] || 0;
      });
      return {
        name: customerLabels[key],
        data,
      };
    });

    return { categories, series, customerDataMap: customerInfoMap };
  };

  const prepareCustomerPieChartData = () => {
    if (!inboundStatistics?.byCustomer) return { labels: [], series: [], customerDataMap: {} };

    const customerCallMap: { [key: string]: number } = {};
    const customerDataMap: { [key: string]: any } = {};

    Object.values(inboundStatistics.byCustomer).forEach((customerData: any) => {
      const customerName = customerData.customer?.name || "Unknown";
      
      if (customerData.calls && Array.isArray(customerData.calls)) {
        const totalCalls = customerData.calls.reduce(
          (sum: number, entry: any) => sum + (entry.calls || 0),
          0
        );
        if (totalCalls > 0) {
          customerCallMap[customerName] = (customerCallMap[customerName] || 0) + totalCalls;
          if (!customerDataMap[customerName] || totalCalls > (customerDataMap[customerName].totalCalls || 0)) {
            customerDataMap[customerName] = { ...customerData.customer, totalCalls };
          }
        }
      }
    });

    return {
      labels: Object.keys(customerCallMap),
      series: Object.values(customerCallMap),
      customerDataMap,
    };
  };

  const calculateCustomerTotals = () => {
    if (!inboundStatistics?.byCustomer) return { totalCalls: 0, totalCustomers: 0 };

    let totalCalls = 0;
    const uniqueCustomers = new Set<string>();

    Object.values(inboundStatistics.byCustomer).forEach((customerData: any) => {
      if (customerData.calls && Array.isArray(customerData.calls)) {
        const calls = customerData.calls.reduce(
          (sum: number, entry: any) => sum + (entry.calls || 0),
          0
        );
        totalCalls += calls;
      }

      const customerId = customerData.customer?.id || "unknown";
      uniqueCustomers.add(customerId);
    });

    return { totalCalls, totalCustomers: uniqueCustomers.size };
  };

  const inboundBarChartData = prepareInboundBarChartData();
  const inboundPieChartData = prepareInboundPieChartData();
  const inboundCostPieChartData = prepareInboundCostPieChartData();
  const { totalCalls: inboundTotalCalls, totalCost: inboundTotalCost, totalTools: inboundTotalTools } = calculateInboundTotals();

  const customerBarChartData = prepareCustomerBarChartData();
  const customerPieChartData = prepareCustomerPieChartData();
  const { totalCalls: customerTotalCalls, totalCustomers } = calculateCustomerTotals();

  // Prepare marketing data: customers and their product diversity
  const prepareMarketingData = () => {
    if (!inboundStatistics?.byCustomer) return [];

    const marketingData: any[] = [];

    Object.values(inboundStatistics.byCustomer).forEach((customerData: any) => {
      const customer = customerData.customer;
      if (customer) {
        // Count unique products/tools used by this customer
        // Since byCustomer doesn't have product info directly, we'll use the number of call entries
        // as a proxy, or we can set it to 1 for now and enhance later if needed
        const productCount = 1; // Placeholder - can be enhanced to count actual unique products
        
        marketingData.push({
          customer: customer,
          customerName: customer.name || "Unknown",
          productCount: productCount,
        });
      }
    });

    return marketingData;
  };

  const marketingData = prepareMarketingData();

  // Marketing table columns
  const marketingColumns: ColumnDef<any>[] = [
    {
      id: "customer",
      size: 200,
      accessorFn: (row: any) => row.customerName.toLowerCase(),
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t("statisticsPanel.marketing.customer")}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="min-w-0 break-words text-sm">{row.original.customerName}</div>
      ),
    },
    {
      id: "productCount",
      size: 150,
      accessorFn: (row: any) => row.productCount,
      header: ({ column }) => (
        <SortableHeader column={column}>
          {t("statisticsPanel.marketing.numberOfProducts")}
        </SortableHeader>
      ),
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">{row.original.productCount}</div>
      ),
    },
    {
      id: "actions",
      size: 150,
      enableSorting: false,
      header: () => t("statisticsPanel.marketing.actions"),
      cell: () => <div>{/* Actions will be added later */}</div>,
    },
  ];

  // Helper function to create offer/plan details dialog content
  const createOfferDetailsContent = (productData: any) => {
    if (!productData) return null;
    
    const offer = productData.offer || {};
    const supplier = productData.supplier || {};
    const plan = productData.plan || {};
    
    const supplierList: any[] = [];
    if (supplier.name) supplierList.push({ label: t("statisticsPanel.name"), value: supplier.name });
    if (supplier.id) supplierList.push({ label: t("statisticsPanel.id"), value: supplier.id });
    if (supplier.domain) supplierList.push({ label: t("statisticsPanel.domain"), value: supplier.domain });
    if (supplier.address) {
      const addr = supplier.address;
      const addressParts = [
        addr.street,
        addr.streetNumber,
        addr.postalCode,
        addr.city,
        addr.country
      ].filter(Boolean);
      if (addressParts.length > 0) {
        supplierList.push({ label: t("statisticsPanel.address"), value: addressParts.join(", ") });
      }
    }
    
    const offerList: any[] = [];
    if (offer.name) offerList.push({ label: t("statisticsPanel.name"), value: offer.name });
    if (offer._type) offerList.push({ label: t("statisticsPanel.type"), value: offer._type });
    if (offer.description) offerList.push({ label: t("statisticsPanel.description"), value: offer.description });
    if (offer.synopsis) offerList.push({ label: t("statisticsPanel.synopsis"), value: offer.synopsis });
    
    // Marketplace Link (under offer InfoList)
    let marketplaceUrl = null;
    if (offer._id) {
      marketplaceUrl = `https://www.real-gain.org/marketplace/${offer._id}`;
      if (plan && plan.voucher) {
        marketplaceUrl += `?voucher=${plan.voucher}`;
      }
    }
    
    const planList: any[] = [];
    if (plan && plan.id) {
      if (plan.name) planList.push({ label: t("statisticsPanel.name"), value: plan.name });
      if (plan.description) planList.push({ label: t("statisticsPanel.description"), value: plan.description });
      if (plan.id) planList.push({ label: t("statisticsPanel.id"), value: plan.id });
      if (plan.baseFee !== undefined) planList.push({ label: t("statisticsPanel.baseFee"), value: currencyFormat.format(plan.baseFee) });
      if (plan.term) planList.push({ label: t("statisticsPanel.term"), value: `${plan.term} ${t("statisticsPanel.months")}` });
      if (plan.billingPeriod) planList.push({ label: t("statisticsPanel.billingPeriod"), value: plan.billingPeriod });
      if (plan.payment) planList.push({ label: t("statisticsPanel.payment"), value: plan.payment });
      if (plan.validFromDate) planList.push({ label: t("statisticsPanel.validFrom"), value: dayjs(plan.validFromDate).format(t("global.dateLong")) });
      if (plan.validToDate) planList.push({ label: t("statisticsPanel.validTo"), value: dayjs(plan.validToDate).format(t("global.dateLong")) });
      
      if (plan.billings && Array.isArray(plan.billings) && plan.billings.length > 0) {
        plan.billings.forEach((billing: any, index: number) => {
          let billingValue = `${currencyFormat.format(billing.fee)}/${t("statisticsPanel.unit")}`;
          if (billing.offset && billing.offset > 0) {
            billingValue += ` (${t("statisticsPanel.offset")}: ${billing.offset})`;
          }
          if (billing.algorithm) {
            billingValue += ` [${billing.algorithm}]`;
          }
          planList.push({ 
            label: billing.name || `${t("statisticsPanel.billings")} ${index + 1}`, 
            value: billingValue 
          });
        });
      }
      
      if (plan.voucher) {
        planList.push({ label: t("statisticsPanel.voucher"), value: plan.voucher });
      }
    }
    
    return (
      <div className="flex min-w-0 flex-col gap-4">
        {supplierList.length > 0 && (
          <div className="min-w-0">
            <h3 className="mb-2 text-base font-semibold">{t("statisticsPanel.supplier")}</h3>
            <InfoList list={supplierList} />
          </div>
        )}
        {offerList.length > 0 && (
          <div className="min-w-0">
            <h3 className="mb-2 text-base font-semibold">{t("statisticsPanel.offer")}</h3>
            <InfoList list={offerList} />
            {marketplaceUrl && (
              <div className="mt-2 min-w-0">
                <a
                  href={marketplaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all text-sm underline underline-offset-4"
                >
                  {marketplaceUrl}
                </a>
              </div>
            )}
          </div>
        )}
        {planList.length > 0 && (
          <div className="min-w-0">
            <h3 className="mb-2 text-base font-semibold">{t("statisticsPanel.license")}</h3>
            <InfoList list={planList} />
          </div>
        )}
      </div>
    );
  };

  // Helper function to create customer details dialog content
  const createCustomerDetailsContent = (customer: any) => {
    if (!customer) return null;
    
    const customerList: any[] = [];
    if (customer.name) customerList.push({ label: t("statisticsPanel.name"), value: customer.name });
    if (customer.id) customerList.push({ label: t("statisticsPanel.id"), value: customer.id });
    if (customer.domain) customerList.push({ label: t("statisticsPanel.domain"), value: customer.domain });
    if (customer.address) {
      const addr = customer.address;
      const addressParts = [
        addr.street,
        addr.streetNumber,
        addr.postalCode,
        addr.city,
        addr.country
      ].filter(Boolean);
      if (addressParts.length > 0) {
        customerList.push({ label: t("statisticsPanel.address"), value: addressParts.join(", ") });
      }
    }
    if (customer.logoURL) {
      customerList.push({ label: t("statisticsPanel.logoURL"), value: customer.logoURL, link: customer.logoURL });
    }
    
    return (
      <div className="min-w-0">
        <h3 className="mb-2 text-base font-semibold">{t("statisticsPanel.buyer")}</h3>
        <InfoList list={customerList} />
      </div>
    );
  };

  // Helper functions to show info dialogs for chart clicks
  const showDetailDialog = (title: string, content: JSX.Element | null) => {
    if (!content) return;
    setDetailDialogTitle(title);
    setDetailDialogContent(content);
    setDetailDialogOpen(true);
  };

  const handleBarChartClick = (event: any, chartContext: any, config: any) => {
    const seriesIndex = config.dataPointIndex !== undefined ? config.seriesIndex : config.selectedDataPoints[0][0];
    const seriesName = barChartData.series[seriesIndex]?.name || "Unknown";
    
    // Find product data for this series
    const seriesKey = Object.keys(barChartData.productDataMap || {}).find(key => {
      const supplierName = barChartData.productDataMap[key]?.supplier?.name || "Unknown";
      const offerName = barChartData.productDataMap[key]?.offer?.name || "Unknown";
      return `${supplierName}: ${offerName}` === seriesName;
    });
    
    const productData = seriesKey ? barChartData.productDataMap[seriesKey] : null;
    const content = createOfferDetailsContent(productData);
    showDetailDialog(seriesName, content);
  };

  const handleInboundBarChartClick = (event: any, chartContext: any, config: any) => {
    const seriesIndex = config.dataPointIndex !== undefined ? config.seriesIndex : config.selectedDataPoints[0][0];
    const seriesName = inboundBarChartData.series[seriesIndex]?.name || "Unknown";
    
    // Find product data for this series
    const seriesKey = Object.keys(inboundBarChartData.productDataMap || {}).find(key => {
      const offerName = inboundBarChartData.productDataMap[key]?.offer?.name || "Unknown";
      return offerName === seriesName;
    });
    
    const productData = seriesKey ? inboundBarChartData.productDataMap[seriesKey] : null;
    const content = createOfferDetailsContent(productData);
    showDetailDialog(seriesName, content);
  };

  const handleCustomerBarChartClick = (event: any, chartContext: any, config: any) => {
    const seriesIndex = config.dataPointIndex !== undefined ? config.seriesIndex : config.selectedDataPoints[0][0];
    const seriesName = customerBarChartData.series[seriesIndex]?.name || "Unknown";
    
    // Find customer data for this series - customerDataMap uses customer IDs as keys
    const customerId = Object.keys(customerBarChartData.customerDataMap || {}).find(key => {
      const customer = customerBarChartData.customerDataMap[key];
      return customer?.name === seriesName;
    });
    
    const customer = customerId ? customerBarChartData.customerDataMap[customerId] : null;
    const content = createCustomerDetailsContent(customer);
    showDetailDialog(seriesName, content);
  };

  const handlePieChartClick = (event: any, chartContext: any, config: any) => {
    const dataPointIndex = config.dataPointIndex;
    const label = pieChartData.labels[dataPointIndex] || "Unknown";
    
    const productData = pieChartData.productDataMap?.[label];
    const content = createOfferDetailsContent(productData);
    showDetailDialog(label, content);
  };

  const handleInboundPieChartClick = (event: any, chartContext: any, config: any) => {
    const dataPointIndex = config.dataPointIndex;
    const label = inboundPieChartData.labels[dataPointIndex] || "Unknown";
    
    const productData = inboundPieChartData.productDataMap?.[label];
    const content = createOfferDetailsContent(productData);
    showDetailDialog(label, content);
  };

  const handleCustomerPieChartClick = (event: any, chartContext: any, config: any) => {
    const dataPointIndex = config.dataPointIndex;
    const label = customerPieChartData.labels[dataPointIndex] || "Unknown";
    
    const customer = customerPieChartData.customerDataMap?.[label];
    const content = createCustomerDetailsContent(customer);
    showDetailDialog(label, content);
  };

  const handleCostPieChartClick = (event: any, chartContext: any, config: any) => {
    const dataPointIndex = config.dataPointIndex;
    const label = costPieChartData.labels[dataPointIndex] || "Unknown";
    
    const productData = costPieChartData.productDataMap?.[label];
    const content = createOfferDetailsContent(productData);
    showDetailDialog(label, content);
  };

  const handleInboundCostPieChartClick = (event: any, chartContext: any, config: any) => {
    const dataPointIndex = config.dataPointIndex;
    const label = inboundCostPieChartData.labels[dataPointIndex] || "Unknown";
    
    const productData = inboundCostPieChartData.productDataMap?.[label];
    const content = createOfferDetailsContent(productData);
    showDetailDialog(label, content);
  };

  const handleLegendClick = (chartContext: any, seriesIndex: number, opts: any) => {
    const label = opts.w.globals.seriesNames[seriesIndex] || "Unknown";
    
    // Find product data for this series
    const seriesKey = Object.keys(barChartData.productDataMap || {}).find(key => {
      const supplierName = barChartData.productDataMap[key]?.supplier?.name || "Unknown";
      const offerName = barChartData.productDataMap[key]?.offer?.name || "Unknown";
      return `${supplierName}: ${offerName}` === label;
    });
    
    const productData = seriesKey ? barChartData.productDataMap[seriesKey] : null;
    const content = createOfferDetailsContent(productData);
    showDetailDialog(label, content);
    return false; // Prevent default legend toggle behavior
  };

  const handlePieChartLegendClick = (labels: string[], series: number[], productDataMap?: any) => {
    return (chartContext: any, seriesIndex: number, _opts: any) => {
      const label = labels[seriesIndex] || "Unknown";
      
      const productData = productDataMap?.[label];
      const content = createOfferDetailsContent(productData);
      showDetailDialog(label, content);
      return false;
    };
  };

  const handleCostPieChartLegendClick = (labels: string[], series: number[], productDataMap?: any) => {
    return (chartContext: any, seriesIndex: number, _opts: any) => {
      const label = labels[seriesIndex] || "Unknown";
      
      const productData = productDataMap?.[label];
      const content = createOfferDetailsContent(productData);
      showDetailDialog(label, content);
      return false;
    };
  };

  const barChartOptions = {
    chart: {
      type: "bar",
      height: 350,
      stacked: true,
      animations: {
        enabled: true,
        speed: 800,
      },
      events: {
        dataPointSelection: handleBarChartClick,
        legendClick: handleLegendClick,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "40%",
        borderRadius: 0,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: barChartData.categories,
    },
    yaxis: {
      title: {
        text: t("billingPanel.toolCalls"),
      },
    },
    fill: {
      opacity: 1,
    },
    colors: colors,
    legend: {
      show: true,
      position: "right" as const,
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val} ${t("billingPanel.calls")}`,
      },
    },
  };

  const pieChartOptions = {
    chart: {
      animations: {
        enabled: true,
        speed: 800,
      },
      events: {
        dataPointSelection: handlePieChartClick,
        legendClick: handlePieChartLegendClick(pieChartData.labels, pieChartData.series, pieChartData.productDataMap),
      },
    },
    legend: {
      show: true,
      position: "bottom" as const,
      offsetY: 0,
      height: undefined,
      width: undefined,
    },
    // Without an explicit formatter ApexCharts renders slice percentages with
    // its built-in `val.toFixed(1)`, i.e. always a decimal *dot* ("76.9%") —
    // next to KPIs formatted through `useFormats` with a comma ("0,00 €").
    // Route the percentage through the same shared formatter.
    dataLabels: {
      formatter: (val: number) => `${numberFormat1Digit.format(val)}%`,
    },
    colors: colors,
    tooltip: {
      y: {
        formatter: (val: number) => `${val} ${t("billingPanel.calls")}`,
      },
    },
  };

  const costPieChartOptions = {
    chart: {
      animations: {
        enabled: true,
        speed: 800,
      },
      events: {
        dataPointSelection: handleCostPieChartClick,
        legendClick: handleCostPieChartLegendClick(costPieChartData.labels, costPieChartData.series, costPieChartData.productDataMap),
      },
    },
    legend: {
      show: true,
      position: "bottom" as const,
    },
    // See `pieChartOptions` — same locale-consistency fix for the cost pie.
    dataLabels: {
      formatter: (val: number) => `${numberFormat1Digit.format(val)}%`,
    },
    colors: colors,
    tooltip: {
      y: {
        formatter: (val: number) => currencyFormat.format(val),
      },
    },
  };

  // Create chart options with event handlers for inbound and customer charts
  const inboundPieChartOptions = {
    ...pieChartOptions,
    labels: inboundPieChartData.labels,
    chart: {
      ...pieChartOptions.chart,
      height: 500,
      events: {
        dataPointSelection: handleInboundPieChartClick,
        legendClick: handlePieChartLegendClick(inboundPieChartData.labels, inboundPieChartData.series, inboundPieChartData.productDataMap),
      },
    },
  };

  const inboundCostPieChartOptions = {
    ...costPieChartOptions,
    labels: inboundCostPieChartData.labels,
    chart: {
      ...costPieChartOptions.chart,
      height: 500,
      events: {
        dataPointSelection: handleInboundCostPieChartClick,
        legendClick: handleCostPieChartLegendClick(inboundCostPieChartData.labels, inboundCostPieChartData.series, inboundCostPieChartData.productDataMap),
      },
    },
  };

  const customerPieChartOptions = {
    ...pieChartOptions,
    labels: customerPieChartData.labels,
    chart: {
      ...pieChartOptions.chart,
      height: 500,
      events: {
        dataPointSelection: handleCustomerPieChartClick,
        legendClick: (chartContext: any, seriesIndex: number, _opts: any) => {
          const label = customerPieChartData.labels[seriesIndex] || "Unknown";
          
          const customer = customerPieChartData.customerDataMap?.[label];
          const content = createCustomerDetailsContent(customer);
          showDetailDialog(label, content);
          return false;
        },
      },
    },
  };

  const inboundBarChartOptions = {
    ...barChartOptions,
    xaxis: {
      ...barChartOptions.xaxis,
      categories: inboundBarChartData.categories,
    },
    chart: {
      ...barChartOptions.chart,
      events: {
        dataPointSelection: handleInboundBarChartClick,
        legendClick: (chartContext: any, seriesIndex: number, opts: any) => {
          const label = opts.w.globals.seriesNames[seriesIndex] || "Unknown";
          
          // Find product data for this series
          const seriesKey = Object.keys(inboundBarChartData.productDataMap || {}).find(key => {
            const offerName = inboundBarChartData.productDataMap[key]?.offer?.name || "Unknown";
            return offerName === label;
          });
          
          const productData = seriesKey ? inboundBarChartData.productDataMap[seriesKey] : null;
          const content = createOfferDetailsContent(productData);
          showDetailDialog(label, content);
          return false;
        },
      },
    },
  };

  const customerBarChartOptions = {
    ...barChartOptions,
    xaxis: {
      ...barChartOptions.xaxis,
      categories: customerBarChartData.categories,
    },
    chart: {
      ...barChartOptions.chart,
      events: {
        dataPointSelection: handleCustomerBarChartClick,
        legendClick: (chartContext: any, seriesIndex: number, opts: any) => {
          const label = opts.w.globals.seriesNames[seriesIndex] || "Unknown";
          
          // Find customer data for this series
          const customerId = Object.keys(customerBarChartData.customerDataMap || {}).find(key => {
            return customerBarChartData.customerDataMap[key]?.name === label;
          });
          
          const customer = customerId ? customerBarChartData.customerDataMap[customerId] : null;
          const content = createCustomerDetailsContent(customer);
          showDetailDialog(label, content);
          return false;
        },
      },
    },
  };

  const selectOfferId = `${fieldId}-offer`;
  const billingPeriodTitle = t("billingPanel.currentBillingPeriodTitle", {
    start: dayjs().startOf("M").format(t("global.dateShort")),
    end: dayjs().endOf("M").format(t("global.dateShort")),
  });

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <Tabs value={tab} onValueChange={setTab} className="min-w-0">
        <TabsList className="max-w-full overflow-x-auto">
          <TabsTrigger value="marketplace">{t("statisticsPanel.marketplace")}</TabsTrigger>
          <TabsTrigger value="inboundCalls">{t("statisticsPanel.inboundCalls")}</TabsTrigger>
          <TabsTrigger value="outboundCalls">{t("statisticsPanel.outboundCalls")}</TabsTrigger>
          <TabsTrigger value="customers">{t("statisticsPanel.customers")}</TabsTrigger>
          <TabsTrigger value="marketing">{t("statisticsPanel.marketing.title")}</TabsTrigger>
        </TabsList>

        <TabsContent value="marketplace" className="mt-4 flex min-w-0 flex-col gap-4">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h3 className="min-w-0 flex-1 text-base font-semibold">
              {t("statisticsPanel.marketplaceStatistics")}
            </h3>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("statisticsPanel.marketplaceRefresh")}
                    disabled={publishedOffersLoading || offerStatisticsLoading}
                    onClick={handleRefreshMarketplace}
                  >
                    <RefreshCw
                      aria-hidden
                      className={
                        publishedOffersLoading || offerStatisticsLoading
                          ? "h-4 w-4 animate-spin"
                          : "h-4 w-4"
                      }
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("statisticsPanel.marketplaceRefresh")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {publishedOffersLoading ? (
            <div className="flex min-w-0 flex-col gap-4">
              <Skeleton className="h-10 w-full max-w-[400px]" />
              <ChartSkeleton />
            </div>
          ) : publishedMarketplaceOffers.length > 0 ? (
            <>
              <div className="flex min-w-0 max-w-[400px] flex-col gap-1.5">
                <Label htmlFor={selectOfferId}>
                  {t("statisticsPanel.marketplaceSelectOffer")}
                </Label>
                <Select
                  value={selectedOfferId ?? NO_OFFER}
                  onValueChange={(value) =>
                    setSelectedOfferId(value === NO_OFFER ? null : value)
                  }
                >
                  <SelectTrigger id={selectOfferId}>
                    <SelectValue placeholder={t("ui.pleaseSelect")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_OFFER}>{t("ui.pleaseSelect")}</SelectItem>
                    {publishedMarketplaceOffers.map((offer) => (
                      <SelectItem key={offer._id} value={offer._id}>
                        {offer.name || offer._id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedOfferId && (
                <>
                  {offerStatisticsLoading ? (
                    <ChartSkeleton />
                  ) : offerStatistics ? (
                    <div className="flex min-w-0 flex-col gap-4">
                      <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
                        <Section title="">
                          <div className="flex flex-col gap-4">
                            <Metric
                              size="large"
                              title={t("statisticsPanel.marketplacePageViewsToday")}
                              value={String(offerStatistics.summary?.pageViewsToday ?? 0)}
                              color={theme.secondary}
                            />
                            <Metric
                              size="large"
                              title={t("statisticsPanel.marketplaceTestClicksToday")}
                              value={String(offerStatistics.summary?.testClicksToday ?? 0)}
                              color={theme.tertiary}
                            />
                          </div>
                        </Section>
                        <Section title="">
                          <div className="flex flex-col gap-4">
                            <Metric
                              size="large"
                              title={t("statisticsPanel.marketplacePageViewsThisMonth")}
                              value={String(offerStatistics.summary?.pageViewsThisMonth ?? 0)}
                              color={theme.secondary}
                            />
                            <Metric
                              size="large"
                              title={t("statisticsPanel.marketplaceTestClicksThisMonth")}
                              value={String(offerStatistics.summary?.testClicksThisMonth ?? 0)}
                              color={theme.tertiary}
                            />
                          </div>
                        </Section>
                        {offerStatistics.summary?.conversionRateThisMonth != null && (
                          <Section title="">
                            <Metric
                              size="large"
                              title={t("statisticsPanel.marketplaceConversionRate")}
                              value={`${numberFormat1Digit.format(offerStatistics.summary.conversionRateThisMonth)}%`}
                              color={theme.primary}
                            />
                          </Section>
                        )}
                      </div>
                      {offerStatistics.pageViewsOverTime?.length > 0 && (
                        <Section title={t("statisticsPanel.marketplacePageViewsOverTime")}>
                          <div className="min-w-0">
                            <ReactApexChart
                              options={{
                                chart: { type: "area" },
                                xaxis: {
                                  categories: offerStatistics.pageViewsOverTime.map((d: any) =>
                                    dayjs(d.date).format(t("global.dateShort"))
                                  ),
                                },
                                yaxis: { title: { text: t("statisticsPanel.marketplacePageViews") } },
                                colors: [theme.secondary],
                                dataLabels: { enabled: false },
                                stroke: { curve: "smooth" },
                              } as any}
                              series={[
                                {
                                  name: t("statisticsPanel.marketplacePageViews"),
                                  data: offerStatistics.pageViewsOverTime.map((d: any) => d.count ?? 0),
                                },
                              ]}
                              type="area"
                              height={300}
                            />
                          </div>
                        </Section>
                      )}
                      {offerStatistics.testClicksOverTime?.length > 0 && (
                        <Section title={t("statisticsPanel.marketplaceTestClicksOverTime")}>
                          <div className="min-w-0">
                            <ReactApexChart
                              options={{
                                chart: { type: "area" },
                                xaxis: {
                                  categories: offerStatistics.testClicksOverTime.map((d: any) =>
                                    dayjs(d.date).format(t("global.dateShort"))
                                  ),
                                },
                                yaxis: { title: { text: t("statisticsPanel.marketplaceTestClicks") } },
                                colors: [theme.tertiary],
                                dataLabels: { enabled: false },
                                stroke: { curve: "smooth" },
                              } as any}
                              series={[
                                {
                                  name: t("statisticsPanel.marketplaceTestClicks"),
                                  data: offerStatistics.testClicksOverTime.map((d: any) => d.count ?? 0),
                                },
                              ]}
                              type="area"
                              height={300}
                            />
                          </div>
                        </Section>
                      )}
                      {offerStatistics.funnel?.steps?.length > 0 && (
                        <Section title={t("statisticsPanel.marketplaceFunnel")}>
                          <div className="min-w-0">
                            <ReactApexChart
                              options={{
                                chart: { type: "bar" },
                                plotOptions: {
                                  bar: { horizontal: true, columnWidth: "60%", borderRadius: 4 },
                                },
                                xaxis: {
                                  categories: offerStatistics.funnel.steps.map((s: any) => s.name),
                                },
                                colors: [theme.primary, theme.secondary, theme.tertiary],
                                dataLabels: { enabled: true },
                              } as any}
                              series={[
                                {
                                  name: t("statisticsPanel.marketplaceCount"),
                                  data: offerStatistics.funnel.steps.map((s: any) => s.count ?? 0),
                                },
                              ]}
                              type="bar"
                              height={Math.max(150, offerStatistics.funnel.steps.length * 60)}
                            />
                          </div>
                        </Section>
                      )}
                      {!offerStatistics.summary && !offerStatistics.pageViewsOverTime?.length && !offerStatistics.funnel?.steps?.length && (
                        <EmptyState
                          title={t("statisticsPanel.noDataTitle")}
                          hint={t("statisticsPanel.marketplaceNoData")}
                        />
                      )}
                    </div>
                  ) : (
                    <EmptyState
                      title={t("statisticsPanel.noDataTitle")}
                      hint={t("statisticsPanel.marketplaceNoData")}
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <EmptyState
              title={t("statisticsPanel.marketplaceNoOffers")}
              hint={t("statisticsPanel.marketplaceNoOffersHint")}
            />
          )}
        </TabsContent>

        <TabsContent value="inboundCalls" className="mt-4 flex min-w-0 flex-col gap-4">
          <h3 className="text-base font-semibold">{billingPeriodTitle}</h3>
          <div className="flex min-w-0 flex-col gap-4 lg:flex-row">
            <Section title="">
              <div className="flex flex-col gap-4">
                {inboundStatistics ? (
                  <>
                    <Metric
                      size="large"
                      title={t("billingPanel.totalCalls")}
                      value={inboundTotalCalls.toLocaleString()}
                      color={theme.secondary}
                    />
                    <Metric
                      size="large"
                      title={t("billingPanel.totalCost")}
                      value={currencyFormat.format(inboundTotalCost)}
                      color={theme.tertiary}
                    />
                    <Metric
                      size="large"
                      title={t("billingPanel.totalOffers")}
                      value={inboundTotalTools.toLocaleString()}
                      color={theme.primary}
                    />
                  </>
                ) : (
                  <MetricsSkeleton count={3} />
                )}
              </div>
            </Section>
            <div className="min-w-0 flex-1">
              <Section title={t("billingPanel.toolCallsPerDay")}>
                {inboundStatistics ? (
                  <div className="min-w-0">
                    <ReactApexChart
                      options={inboundBarChartOptions as any}
                      series={inboundBarChartData.series}
                      type="bar"
                      height={300}
                    />
                  </div>
                ) : (
                  <ChartSkeleton />
                )}
              </Section>
            </div>
          </div>
          <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
            <Section title={t("billingPanel.callsPerOffer")}>
              {inboundStatistics && inboundPieChartData.series.length > 0 ? (
                <div className="min-w-0 pb-5">
                  <ReactApexChart
                    options={inboundPieChartOptions as any}
                    series={inboundPieChartData.series}
                    type="pie"
                    height={500}
                  />
                </div>
              ) : inboundStatistics ? (
                <EmptyState
                  title={t("billingPanel.noDataAvailable")}
                  hint={t("statisticsPanel.noCallsHint")}
                />
              ) : (
                <ChartSkeleton className="h-[500px] w-full" />
              )}
            </Section>
            <Section title={t("billingPanel.costsPerOffer")}>
              {inboundStatistics && inboundCostPieChartData.series.length > 0 ? (
                <div className="min-w-0 pb-5">
                  <ReactApexChart
                    options={inboundCostPieChartOptions as any}
                    series={inboundCostPieChartData.series}
                    type="pie"
                    height={500}
                  />
                </div>
              ) : inboundStatistics ? (
                <EmptyState
                  title={t("billingPanel.noDataAvailable")}
                  hint={t("statisticsPanel.noCallsHint")}
                />
              ) : (
                <ChartSkeleton className="h-[500px] w-full" />
              )}
            </Section>
          </div>
        </TabsContent>

        <TabsContent value="outboundCalls" className="mt-4 flex min-w-0 flex-col gap-4">
          <h3 className="text-base font-semibold">{billingPeriodTitle}</h3>
          <div className="flex min-w-0 flex-col gap-4 lg:flex-row">
            <Section title="">
              <div className="flex flex-col gap-4">
                {meteringStatistics ? (
                  <>
                    <Metric
                      size="large"
                      title={t("billingPanel.totalCalls")}
                      value={totalCalls.toLocaleString()}
                      color={theme.secondary}
                    />
                    <Metric
                      size="large"
                      title={t("billingPanel.totalCost")}
                      value={currencyFormat.format(totalCost)}
                      color={theme.tertiary}
                    />
                    <Metric
                      size="large"
                      title={t("billingPanel.totalOffers")}
                      value={totalOffers.toLocaleString()}
                      color={theme.primary}
                    />
                  </>
                ) : (
                  <MetricsSkeleton count={3} />
                )}
              </div>
            </Section>
            <div className="min-w-0 flex-1">
              <Section title={t("billingPanel.toolCallsPerDay")}>
                {meteringStatistics ? (
                  <div className="min-w-0">
                    <ReactApexChart
                      options={barChartOptions as any}
                      series={barChartData.series}
                      type="bar"
                      height={300}
                    />
                  </div>
                ) : (
                  <ChartSkeleton />
                )}
              </Section>
            </div>
          </div>
          <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
            <Section title={t("billingPanel.callsPerOffer")}>
              {meteringStatistics && pieChartData.series.length > 0 ? (
                <div className="min-w-0 pb-5">
                  <ReactApexChart
                    options={{ ...pieChartOptions, labels: pieChartData.labels, chart: { ...pieChartOptions.chart, height: 500 } } as any}
                    series={pieChartData.series}
                    type="pie"
                    height={500}
                  />
                </div>
              ) : meteringStatistics ? (
                <EmptyState
                  title={t("billingPanel.noDataAvailable")}
                  hint={t("statisticsPanel.noCallsHint")}
                />
              ) : (
                <ChartSkeleton className="h-[500px] w-full" />
              )}
            </Section>
            <Section title={t("billingPanel.costsPerOffer")}>
              {meteringStatistics && costPieChartData.series.length > 0 ? (
                <div className="min-w-0 pb-5">
                  <ReactApexChart
                    options={{ ...costPieChartOptions, labels: costPieChartData.labels, chart: { ...costPieChartOptions.chart, height: 500 } } as any}
                    series={costPieChartData.series}
                    type="pie"
                    height={500}
                  />
                </div>
              ) : meteringStatistics ? (
                <EmptyState
                  title={t("billingPanel.noDataAvailable")}
                  hint={t("statisticsPanel.noCallsHint")}
                />
              ) : (
                <ChartSkeleton className="h-[500px] w-full" />
              )}
            </Section>
          </div>
        </TabsContent>

        <TabsContent value="customers" className="mt-4 flex min-w-0 flex-col gap-4">
          <h3 className="text-base font-semibold">{billingPeriodTitle}</h3>
          <div className="flex min-w-0 flex-col gap-4 lg:flex-row">
            <Section title="">
              <div className="flex flex-col gap-4">
                {inboundStatistics ? (
                  <>
                    <Metric
                      size="large"
                      title={t("billingPanel.totalCalls")}
                      value={customerTotalCalls.toLocaleString()}
                      color={theme.secondary}
                    />
                    <Metric
                      size="large"
                      title={t("billingPanel.totalCustomers")}
                      value={totalCustomers.toLocaleString()}
                      color={theme.primary}
                    />
                  </>
                ) : (
                  <MetricsSkeleton count={2} />
                )}
              </div>
            </Section>
            <div className="min-w-0 flex-1">
              <Section title={t("billingPanel.toolCallsPerDay")}>
                {inboundStatistics ? (
                  <div className="min-w-0">
                    <ReactApexChart
                      options={customerBarChartOptions as any}
                      series={customerBarChartData.series}
                      type="bar"
                      height={300}
                    />
                  </div>
                ) : (
                  <ChartSkeleton />
                )}
              </Section>
            </div>
          </div>
          <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
            <Section title={t("billingPanel.callsPerCustomer")}>
              {inboundStatistics && customerPieChartData.series.length > 0 ? (
                <div className="min-w-0 pb-5">
                  <ReactApexChart
                    options={customerPieChartOptions as any}
                    series={customerPieChartData.series}
                    type="pie"
                    height={500}
                  />
                </div>
              ) : inboundStatistics ? (
                <EmptyState
                  title={t("billingPanel.noDataAvailable")}
                  hint={t("statisticsPanel.noCallsHint")}
                />
              ) : (
                <ChartSkeleton className="h-[500px] w-full" />
              )}
            </Section>
          </div>
        </TabsContent>

        <TabsContent value="marketing" className="mt-4 min-w-0">
          {inboundStatistics ? (
            <div className="min-w-0 overflow-x-auto">
              <DataTable
                columns={marketingColumns}
                data={marketingData}
                page={marketingPagination.page}
                onPageChange={marketingPagination.onPageChange}
                previousAriaLabel={t("global.back")}
                nextAriaLabel={t("global.next")}
                emptyMessage={
                  <EmptyState
                    title={t("statisticsPanel.marketing.noCustomersTitle")}
                    hint={t("statisticsPanel.marketing.noCustomersHint")}
                  />
                }
              />
            </div>
          ) : (
            <ChartSkeleton className="h-[240px] w-full" />
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog: Detailangaben zu einer Diagrammreihe */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent
          className="max-h-[85vh] max-w-[calc(100vw-2rem)] overflow-y-auto sm:max-w-[800px]"
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="min-w-0 break-words">{detailDialogTitle}</DialogTitle>
          </DialogHeader>
          <div className="min-w-0">{detailDialogContent}</div>
          <DialogFooter className="pt-4">
            <Button onClick={() => setDetailDialogOpen(false)}>{t("global.ok")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
